import { useEffect, useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCycleWorkItemsByCycle } from '@/lib/db/rxdb/hooks/entities/cycleWorkItems'
import { useCycleMutations, useProjectCycles } from '@/lib/db/rxdb/hooks/entities/cycles'
import { useTasks } from '@/lib/db/rxdb/hooks'
import { completeCycle } from '@/lib/api/features/cycles/api'
import useLoginStore from '@/store/LoginStore'

interface CompleteCycleDialogProps {
  cycleId: string
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete?: () => void
}

export function CompleteCycleDialog({ cycleId, projectId, open, onOpenChange, onComplete }: CompleteCycleDialogProps) {
  const { t } = useTranslation('projects')
  const { cycleWorkItems } = useCycleWorkItemsByCycle(cycleId)
  const { cycles } = useProjectCycles(projectId)
  const { tasks } = useTasks()
  const { updateCycle } = useCycleMutations()
  const [action, setAction] = useState<'carry_over' | 'backlog'>('carry_over')
  const [nextCycleId, setNextCycleId] = useState<string>('')
  const [isCompleting, setIsCompleting] = useState(false)
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const draftCycles = useMemo(
    () => cycles?.filter((c) => c.status === 'draft').sort((a, b) => a.number - b.number) ?? [],
    [cycles],
  )

  const incompleteCount = useMemo(() => {
    if (!cycleWorkItems || !tasks) return 0
    const workItemIds = new Set(cycleWorkItems.map((cwi) => cwi.workItemId))
    return tasks.filter((t) => {
      if (!workItemIds.has(t.id) || t.isDeleted) return false
      if (t.projectId !== projectId) return false
      const progressType = t.status?.progressType
      return progressType !== 'CD' && progressType !== 'CL'
    }).length
  }, [cycleWorkItems, tasks, projectId])

  // Pre-select the lowest-numbered draft cycle as the carry-over target.
  useEffect(() => {
    const firstDraft = draftCycles[0]
    if (!nextCycleId && firstDraft) {
      setNextCycleId(firstDraft.id)
    }
  }, [draftCycles, nextCycleId])

  // Force backlog if no draft cycles exist (carry_over has no valid target).
  useEffect(() => {
    if (incompleteCount > 0 && draftCycles.length === 0 && action === 'carry_over') {
      setAction('backlog')
    }
  }, [incompleteCount, draftCycles.length, action])

  const handleComplete = async () => {
    if (!workspaceId) return
    setIsCompleting(true)
    try {
      const effectiveAction = incompleteCount === 0 ? 'backlog' : action
      const response = await completeCycle(workspaceId, cycleId, {
        incompleteAction: effectiveAction,
        nextCycleId: effectiveAction === 'carry_over' && nextCycleId ? nextCycleId : undefined,
      })

      // Optimistic local status flip — server CompletedOn/CompletedBy will arrive on next pull.
      // Don't write completedOn/completedBy locally: RxDB stamps updatedOn=Date.now() which
      // would push later and overwrite the server's authoritative completion timestamp.
      await updateCycle(cycleId, { status: 'completed' })

      const carriedOverCount = response.carriedOverCount ?? 0
      if (carriedOverCount > 0 && nextCycleId) {
        const nextCycleName = cycles?.find((c) => c.id === nextCycleId)?.name ?? ''
        toast.success(t('cycles.toasts.completedWithCarryOver', { count: carriedOverCount, cycleName: nextCycleName }))
      } else {
        toast.success(t('cycles.toasts.completed'))
      }

      onOpenChange(false)
      onComplete?.()
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 403) {
        toast.error(t('cycles.toasts.permissionDenied'))
      } else {
        toast.error(t('cycles.toasts.failed'))
      }
    } finally {
      setIsCompleting(false)
    }
  }

  const noDraftCycles = draftCycles.length === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('cycles.completeDialog.title')}</DialogTitle>
          <DialogDescription>
            {incompleteCount > 0
              ? t('cycles.completeDialog.descriptionWithIncomplete', { count: incompleteCount })
              : t('cycles.completeDialog.descriptionAllDone')}
          </DialogDescription>
        </DialogHeader>
        {incompleteCount > 0 && (
          <div className="space-y-4 py-2">
            <div className="space-y-3">
              <label className={`flex items-start gap-3 ${noDraftCycles ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                <input
                  type="radio"
                  name="action"
                  checked={action === 'carry_over'}
                  onChange={() => setAction('carry_over')}
                  disabled={noDraftCycles}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium">{t('cycles.completeDialog.moveToNext')}</p>
                  <p className="text-xs text-muted-foreground">
                    {noDraftCycles
                      ? t('cycles.completeDialog.noDraftCycles')
                      : t('cycles.completeDialog.moveToNextDescription')}
                  </p>
                </div>
              </label>
              {action === 'carry_over' && !noDraftCycles && (
                <div className="ml-6">
                  <Label className="text-xs">{t('cycles.completeDialog.targetCycle')}</Label>
                  <Select value={nextCycleId} onValueChange={setNextCycleId}>
                    <SelectTrigger className="h-8 text-base mt-1 md:text-sm">
                      <SelectValue placeholder={t('cycles.completeDialog.selectCycle')} />
                    </SelectTrigger>
                    <SelectContent>
                      {draftCycles.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="action"
                  checked={action === 'backlog'}
                  onChange={() => setAction('backlog')}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium">{t('cycles.completeDialog.moveToBacklog')}</p>
                  <p className="text-xs text-muted-foreground">{t('cycles.completeDialog.moveToBacklogDescription')}</p>
                </div>
              </label>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('cycles.completeDialog.cancel')}
          </Button>
          <Button
            onClick={handleComplete}
            disabled={isCompleting || (action === 'carry_over' && incompleteCount > 0 && !nextCycleId)}
          >
            {isCompleting ? t('cycles.completing') : t('cycles.completeDialog.title')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
